import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '../../_core/logger.service';
import { ConfigService } from '../../_core/service/config.service';
import {
  FormType,
  PDF_CUSTOM_STATE_ORDER,
  PDFGenerationStatus,
  PIP_MASTER_STATES_LIST,
} from '../../config/constants';
import { PipBaseRulesService as PIPBaseRulesService } from '../../state-disclosures/pip/services/pip-base-rules.service';
import { UimuiBaseRulesService as UMUIMBaseRulesService } from '../../state-disclosures/uimui/services/uimui-base-rules.service';
import { RequestService } from '../request/request.service';

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  signatureObject = null;

  pipKeysToBeReplaced = {
    hawaii: {
      key: 'additionalchiropractictreatments_the',
      replaceWith: 'additionalchiropractictreatments',
    },
  };

  /**
   * check to see if GDMS generated the pdf. if yes, use that. else generate the pdf again.
   */

  // request_sent, response_received, error
  uiumPdfGenerationStatus: PDFGenerationStatus =
    PDFGenerationStatus.RequestNotSentYet;
  pipPdfGenerationStatus: PDFGenerationStatus =
    PDFGenerationStatus.RequestNotSentYet;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private umuimBaseRulesService: UMUIMBaseRulesService,
    private pipBaseRulesService: PIPBaseRulesService,
    private requestService: RequestService,
  ) {}

  sortStates(statesAr) {
    // Use PDF_CUSTOM_STATE_ORDER for custom sorting
    return statesAr.sort((a, b) => {
      const idxA = PDF_CUSTOM_STATE_ORDER.findIndex(
        (s) => s.toLowerCase().replace(/[-\s]/g, '') === a.state.toLowerCase(),
      );
      const idxB = PDF_CUSTOM_STATE_ORDER.findIndex(
        (s) => s.toLowerCase().replace(/[-\s]/g, '') === b.state.toLowerCase(),
      );
      if (idxA === -1 && idxB === -1) return 0;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }
  private getInsuredPhoneNumber(): string {
    const requestPhone =
      this.requestService.request?.requestDetails?.accountDetail?.phoneNumber;

    let umuimPhone = '';
    if (this.umuimBaseRulesService.dataDump) {
      umuimPhone =
        this.umuimBaseRulesService.dataDump['data']?.['unSelectBusinessNum'] ||
        this.umuimBaseRulesService.dataDump['unSelectBusinessNum'] ||
        '';
    }

    let pipPhone = '';
    if (this.pipBaseRulesService.dataDump) {
      pipPhone =
        this.pipBaseRulesService.dataDump['data']?.['pipSelectBusinessNum'] ||
        this.pipBaseRulesService.dataDump['pipSelectBusinessNum'] ||
        '';
    }

    const dataDumpPhone = umuimPhone || pipPhone;

    return requestPhone || dataDumpPhone || '';
  }
  clearUMUIMDataForNonDigitalFlow(): void {
    this.umuimBaseRulesService.pdfDataDump = {};
    this.umuimBaseRulesService.dataDump['outstandingStates'] = [];
    this.umuimBaseRulesService.savedBaseRules = {};
  }
  clearPIPDataForNonDigitalFlow(): void {
    this.pipBaseRulesService.pdfDataDump = {};
    this.pipBaseRulesService.dataDump['outstandingStates'] = [];
    this.pipBaseRulesService.savedBaseRules = {};
  }
  getReviewStateForm(formType, electronicConsent: boolean = true) {
    Logger.log('Start of review UM UIM State forms');
    let inProgressFlag =
      formType === FormType.UM_UIM
        ? 'downloadUIUMFormsInProgress'
        : 'downloadPipFormsInProgress';

    let obj = {};
    let coveredStates = this.requestService.getCoveredStates();
    let request = this.requestService.request;
    let policyNumber = request?.requestDetails?.policyDetail?.[0]?.number || '';
    let policyEffDate =
      request?.requestDetails?.policyDetail?.[0]?.effectiveDate || '';

    let namedInsured =
      request?.requestDetails?.accountDetail?.insuredName || '';
    if (formType === FormType.UM_UIM) {
      obj = this.umuimBaseRulesService.pdfDataDump;
    } else {
      obj = this.pipBaseRulesService.pdfDataDump;
      coveredStates = coveredStates.filter((state) =>
        PIP_MASTER_STATES_LIST.map((s) =>
          s.toLowerCase().replace(/\s/g, ''),
        ).includes(state.toLowerCase().replace(/\s/g, '')),
      );
    }

    let statesWithMultipleForms = {
      pennsylvania: ['62585', '62598'],
      virginia: ['62604', '148621'],
      westvirginia: ['78927', '78929', '78931', '78928', '78930'],
      iowa: ['62590', '119200'],
      districtofcolumbia: ['119199', '63583'],
    };

    let keys = Object.keys(obj);

    let ar: { state: string; data: any }[] = [];
    for (let key of keys) {
      let val = obj[key];
      let trimmedState = key.toLowerCase().replace(/[-\s]/g, '');
      if (
        formType === FormType.UM_UIM &&
        statesWithMultipleForms.hasOwnProperty(trimmedState)
      ) {
        let ar1 = statesWithMultipleForms[trimmedState];
        for (let formId of ar1) {
          let pdfObj = {
            state: `${trimmedState}_${formId}`,
            data: {
              ...val,
              effectiveDate: policyEffDate,
              policyNumber:
                this.requestService.getPolicyNumberForState(trimmedState),
              issuingCompany:
                this.requestService.getIssuingCompanyForState(trimmedState), // Add this line
            },
          };
          ar.push(pdfObj);
        }
        this.removenosignform(ar);
      } else {
        let pdfObj = {
          state: key.toLowerCase().replace(/[-\s]/g, ''),
          data: {
            ...val,
            effectiveDate: policyEffDate,
            policyNumber:
              this.requestService.getPolicyNumberForState(trimmedState),
            issuingCompany:
              this.requestService.getIssuingCompanyForState(trimmedState), // Add this line
          },
        };
        ar.push(pdfObj);
      }
    }

    let states = ar;
    if (states.length === 0) {
      for (let state of coveredStates) {
        let trimmedState = state.toLowerCase().replace(/[-\s]/g, '');
        if (
          formType === FormType.UM_UIM &&
          statesWithMultipleForms.hasOwnProperty(trimmedState)
        ) {
          let ar1 = statesWithMultipleForms[trimmedState];
          for (let formId of ar1) {
            states.push({
              state: `${trimmedState}_${formId}`,
              data: {
                namedInsured: namedInsured,
                policyNumber:
                  this.requestService.getPolicyNumberForState(trimmedState),
                nameTitle: `${namedInsured} (${request?.requestDetails?.accountDetail?.title})`,
                effectiveDate: policyEffDate,
                issuingCompany:
                  this.requestService.getIssuingCompanyForState(trimmedState),
                isSignatureImagePNG: false,
                signatureImage: '',
                method: 'type',
              },
            });
          }
        } else {
          states.push({
            state: trimmedState,
            data: {
              namedInsured: namedInsured,
              policyNumber:
                this.requestService.getPolicyNumberForState(trimmedState),
              nameTitle: `${namedInsured} (${request?.requestDetails?.accountDetail?.title})`,
              effectiveDate: policyEffDate,
              issuingCompany:
                this.requestService.getIssuingCompanyForState(trimmedState),
              isSignatureImagePNG: false,
              signatureImage: '',
              method: 'type',
            },
          });
        }
      }
    }
    states = this.sortStates(states);
    // Filter coveredStates for PIP using PIP_MASTER_STATES_LIST
    const policyInfo = this.getFormattedPolicyInfo();
    const reqObj = {
      formType: formType,
      states: states,
      signature: {
        namedInsured: namedInsured,
        policyNumber: policyNumber,
        nameTitle: `${namedInsured} (${request?.requestDetails?.accountDetail?.title})`,
        effectiveDate: policyEffDate,
        isSignatureImagePNG: false,
        signatureImage: '',
        method: 'type',
      },
      policyInfo: policyInfo,
      electronicConsent: electronicConsent,
      underwriterName: request?.requestDetails?.underWriterDetail?.name,
      underwriterAddress: this.getFormattedUnderwriterAddress(),
      insuredAddress: this.getFormattedInsuredMailingAddress(),
      operations: request?.requestDetails?.underWriterDetail?.operations,
      insuredTitle: request?.requestDetails?.accountDetail?.title,
      companyName: request?.requestDetails?.accountDetail?.insuredCompanyName,
      insuredCompanyName:
        request?.requestDetails?.accountDetail?.insuredCompanyName,
      eventType: request?.requestDetails?.policySelection?.eventType,
      insuredName: request?.requestDetails?.accountDetail?.insuredName,
      brokerName: request?.requestDetails?.accountDetail?.brokerName,
      brokerEmail: request?.requestDetails?.accountDetail?.brokerEmail,
      insuredEmail: request?.requestDetails?.accountDetail?.insuredEmail,
      insuredKey: this.requestService.request.insuredKey,
      uwEmail: request?.requestDetails?.underWriterDetail?.email,
      insuredPhoneNumber: this.getInsuredPhoneNumber(),
    };

    return this.generatePreview(reqObj);
  }

  generatePreview(requestObj) {
    Logger.log('Start of generatePreview');
    let apiBaseUrl = this.configService.getConfig().pdfBaseUrl;
    return this.http.post(`${apiBaseUrl}/api/multistate/preview`, requestObj, {
      responseType: 'arraybuffer',
    });
  }

  removeUnwantedCharacters(formType, obj) {
    const cleanedObj = {};
    for (const key in obj) {
      let val = obj[key];
      if (typeof val === 'object' && val !== null) {
        for (const innerKey in val) {
          if (!/^[a-zA-Z0-9_]+$/.test(innerKey)) {
            const cleanedKey = innerKey.replace(/[^a-zA-Z0-9_]/g, '');
            if (cleanedKey !== innerKey) {
              console.log(
                `Remove unwanted characters. Form Type: ${formType}, Key: ${key}, Inner Key: ${innerKey}, Cleaned Key: ${cleanedKey}`,
              );
              if (
                formType === FormType.PIP &&
                Object.keys(this.pipKeysToBeReplaced).some((state) =>
                  key.toLowerCase().includes(state),
                )
              ) {
                const matchedState = Object.keys(this.pipKeysToBeReplaced).find(
                  (state) => key.toLowerCase().includes(state),
                );
                const replacement = this.pipKeysToBeReplaced[matchedState];
                if (
                  innerKey
                    .toLowerCase()
                    .search(replacement.key.toLowerCase()) !== -1
                ) {
                  val[replacement.replaceWith] = val[innerKey];
                  delete val[innerKey];
                  continue;
                }
              }
            }
          }
        }
      }
    }
  }
  getFormattedPolicyInfo() {
    const policyDetails =
      this.requestService.request?.requestDetails?.policyDetail || [];
    // Format policy data
    const policyInfo = {
      policies: policyDetails.map((policy) => ({
        policyNumber: policy.number || '',
        effectiveDate: policy.effectiveDate || '',
        expirationDate: policy.expirationDate || '',
      })),
    };

    return policyInfo;
  }
  getFormattedUnderwriterAddress(): string {
    const underWriterDetail =
      this.requestService.request?.requestDetails?.underWriterDetail;
    if (!underWriterDetail) {
      return '';
    }
    const { address, city, state } = underWriterDetail;
    let formattedAddress = '';
    if (address) {
      formattedAddress += address;
    }
    if (city) {
      formattedAddress += formattedAddress ? `, ${city}` : city;
    }
    if (state) {
      formattedAddress += formattedAddress ? `, ${state}` : state;
    }
    return formattedAddress;
  }
  getFormattedInsuredMailingAddress(): string {
    const accountDetail =
      this.requestService.request?.requestDetails?.accountDetail;
    if (!accountDetail) {
      return '';
    }
    const { address, city, state, zipCode } = accountDetail;
    let formattedAddress = '';
    if (address) {
      formattedAddress += address;
    }
    if (city) {
      formattedAddress += formattedAddress ? `, ${city}` : city;
    }
    if (state) {
      formattedAddress += formattedAddress ? `, ${state}` : state;
    }

    if (zipCode) {
      formattedAddress += formattedAddress ? ` ${zipCode}` : zipCode;
    }

    return formattedAddress;
  }

  getPdfFromGDMS(formType, insuredKey, insuredEmail?: string) {
    let apiBaseUrl = this.configService.getConfig().pdfBaseUrl;
    const resolvedInsuredEmail =
      insuredEmail ??
      this.requestService.request?.requestDetails?.accountDetail?.insuredEmail;

    const payload = {
      formType,
      insuredKey,
      insuredEmail: resolvedInsuredEmail,
    };

    return this.http.post(`${apiBaseUrl}/api/multistate/getpdf`, payload, {
      responseType: 'arraybuffer',
    });
  }


  generateUMUIMSendEmailPdf(
    electronicConsent: boolean = true,
    emailType: string = 'thankyou',
  ) {
    Logger.log('Start of generateUMUIMSendEmailPdf');

    let obj = this.umuimBaseRulesService.pdfDataDump;
    this.removeUnwantedCharacters(FormType.UM_UIM, obj);
    let keys = Object.keys(obj);

    let excludeStates = [];

    let statesWithMultipleForms = {
      pennsylvania: ['62585', '62598'],
      virginia: ['148621', '62604'],
      westvirginia: ['78927', '78929', '78931', '78928', '78930'],
      iowa: ['62590', '119200'],
      districtofcolumbia: ['119199', '63583'],
    };

    let ar: { state: string; data: any }[] = [];
    for (let key of keys) {
      if (excludeStates.includes(key.toLowerCase())) {
        continue;
      }
      let val = obj[key];
      let trimmedState = key.toLowerCase().replace(/[-\s]/g, '');
      if (statesWithMultipleForms.hasOwnProperty(trimmedState)) {
        let ar1 = statesWithMultipleForms[trimmedState];
        for (let formId of ar1) {
          let pdfObj = {
            state: `${trimmedState}_${formId}`,
            data: {
              ...val,
              namedInsured:
                this.requestService.request?.requestDetails?.accountDetail
                  ?.insuredName || '',
              policyNumber:
                this.requestService.getPolicyNumberForState(trimmedState),
              nameTitle: `${this.requestService.request?.requestDetails?.accountDetail?.insuredName} (${this.requestService.request?.requestDetails?.accountDetail?.title})`,
              effectiveDate:
                this.requestService.request?.requestDetails?.policyDetail?.[0]
                  ?.effectiveDate || '',
              issuingCompany:
                this.requestService.getIssuingCompanyForState(trimmedState), // Add this line
              isSignatureImagePNG: false,
              signatureImage: '',
              method: 'type',
            },
          };
          ar.push(pdfObj);
        }
        this.removenosignform(ar);
      } else {
        let pdfObj = {
          state: key.toLowerCase().replace(/[-\s]/g, ''),
          data: {
            ...val,
            namedInsured:
              this.requestService.request?.requestDetails?.accountDetail
                ?.insuredName || '',
            policyNumber:
              this.requestService.getPolicyNumberForState(trimmedState),
            nameTitle: `${this.requestService.request?.requestDetails?.accountDetail?.insuredName} (${this.requestService.request?.requestDetails?.accountDetail?.title})`,
            effectiveDate:
              this.requestService.request?.requestDetails?.policyDetail?.[0]
                ?.effectiveDate || '',
            issuingCompany:
              this.requestService.getIssuingCompanyForState(trimmedState), // Add this line
            isSignatureImagePNG: false,
            signatureImage: '',
            method: 'type',
          },
        };
        ar.push(pdfObj);
      }
    }

    if (ar.length === 0) {
      // if the user wants to download the empty forms, in the uimui-thankyou route.
      ar = this.generateBlankData(FormType.UM_UIM);
    }
    // if the user wants to download the empty forms, in the uimui-thankyou route.
    ar = this.sortStates(ar);
    let apiBaseUrl = this.configService.getConfig().pdfBaseUrl;
    const policyInfo = this.getFormattedPolicyInfo();
    const reqObj = {
      formType: 'um_uim',
      states: ar,
      signature: this.signatureObject,
      emailType: emailType,
      policyInfo: policyInfo,
      brokerCompanyName:
        this.requestService.request.requestDetails.accountDetail
          ?.brokerCompanyName,
      brokerEmail:
        this.requestService.request.requestDetails.accountDetail?.brokerEmail,
      underwriterName:
        this.requestService.request.requestDetails.underWriterDetail?.name,
      electronicConsent: electronicConsent,
      underwriterAddress: this.getFormattedUnderwriterAddress(),
      insuredAddress: this.getFormattedInsuredMailingAddress(),
      operations:
        this.requestService.request.requestDetails.underWriterDetail
          ?.operations,
      insuredTitle:
        this.requestService.request.requestDetails.accountDetail?.title,
      companyName:
        this.requestService.request.requestDetails.accountDetail
          .insuredCompanyName,
      insuredCompanyName:
        this.requestService.request.requestDetails.accountDetail
          .insuredCompanyName,
      eventType:
        this.requestService.request.requestDetails.policySelection?.eventType,
      insuredName:
        this.requestService.request.requestDetails.accountDetail?.insuredName,
      brokerName:
        this.requestService.request.requestDetails.accountDetail?.brokerName,
      uwEmail:
        this.requestService.request.requestDetails.underWriterDetail?.email,
      insuredEmail:
        this.requestService.request.requestDetails.accountDetail?.insuredEmail,
      insuredKey: this.requestService.request.insuredKey,
      insuredPhoneNumber: this.getInsuredPhoneNumber(),
    };
    return this.http.post(
      `${apiBaseUrl}/notification/sendEmailWithPDF`,
      reqObj,
      {
        responseType: 'arraybuffer',
      },
    );
  }
  //remove unsigned forms only the states having multiple forms
  private removenosignform(pdfobj) {
    for (let state of pdfobj) {
      if (state.state == 'westvirginia_78928') {
        if (
          state.data.umsplit_signature_levelthree_radio !== true &&
          state.data.splitlimits_umbiumpd_leveltwo_radio !== true
        ) {
          state.data.removeForm = true;
        }
      }
      if (state.state == 'westvirginia_78929') {
        if (
          state.data.uimsplit_signature_levelthree_radio !== true &&
          state.data.splitlimits_uimbiuimpd_leveltwo_radio !== true &&
          state.data.splitreject_uimbiuimpd_leveltwo_radio !== true
        ) {
          state.data.removeForm = true;
        }
      }
      if (state.state == 'westvirginia_78930') {
        if (
          state.data.umcsl_signature_levelthree_radio !== true &&
          state.data.combinedsinglelimit_umbiumpd_leveltwo_radio !== true
        ) {
          state.data.removeForm = true;
        }
      }
      if (state.state == 'westvirginia_78931') {
        if (
          state.data.uimcsl_signature_levelthree_radio !== true &&
          state.data.combinedsinglelimit_uimbiuimpd_leveltwo_radio !== true &&
          state.data.rejectuim_uimbiuimpd_leveltwo_radio !== true
        ) {
          state.data.removeForm = true;
        }
      }
      if (state.state == 'iowa_62590') {
        if (
          state.data.uimreject_levelone_umuim_radio !== true &&
          state.data.umuimselect_levelone_umuim_radio !== true &&
          state.data.um_combinedsingleminlimit_40k !== true &&
          state.data.um_splitminlimits_20k_40k !== true &&
          state.data.limits_leveloneum_radio !== true &&
          state.data.umreject_levelone_umuim_radio !== true &&
          state.data.umuimselect_levelone_umuim_radio !== true &&
          state.data.uim_combinedsingleminlimit_40k !== true &&
          state.data.uim_splitminlimits_20k_40k !== true &&
          state.data.limits_leveloneuim_radio !== true &&
          state.data
            .uim_liabilitylimit_Limit_equal_to_the_limit_of_my_Bodily_Injury_Liability_Coverage !==
            true &&
          state.data
            .um_liabilitylimit_Limit_equal_to_the_limit_of_my_Bodily_Injury_Liability_Coverage !==
            true
        ) {
          state.data.removeForm = true;
        }
      }
      if (state.state == 'iowa_119200') {
        if (
          state.data.reject_levelone_um_radio !== true &&
          state.data.umreject_levelone_umuim_radio !== true &&
          state.data.umuimreject_levelone_umuim_radio !== true &&
          state.data.reject_leveloneum_radio !== true &&
          state.data.reject_levelone_uim_radio !== true &&
          state.data.uimreject_levelone_umuim_radio !== true &&
          state.data.umuimreject_levelone_umuim_radio !== true &&
          state.data.reject_leveloneuim_radio !== true
        ) {
          state.data.removeForm = true;
        }
      }
    }
  }

  generateBlankData(formType: FormType) {
    Logger.log('Start of generateBlankData');
    let request = this.requestService.request;
    let policyNumber = request?.requestDetails?.policyDetail?.[0]?.number || '';
    let policyEffDate =
      request?.requestDetails?.policyDetail?.[0]?.effectiveDate || '';
    let coveredStates = this.requestService.getCoveredStates();

    let namedInsured =
      request?.requestDetails?.accountDetail?.insuredName || '';

    let obj = {};

    if (formType === FormType.UM_UIM) {
      obj = this.umuimBaseRulesService.pdfDataDump;
    } else {
      obj = this.pipBaseRulesService.pdfDataDump;
      coveredStates = coveredStates.filter((state) =>
        PIP_MASTER_STATES_LIST.map((s) =>
          s.toLowerCase().replace(/\s/g, ''),
        ).includes(state.toLowerCase().replace(/\s/g, '')),
      );
    }
    let statesWithMultipleForms = {
      pennsylvania: ['62585', '62598'],
      virginia: ['62604', '148621'],
      westvirginia: ['78927', '78929', '78931', '78928', '78930'],
      iowa: ['62590', '119200'],
      districtofcolumbia: ['119199', '63583'],
    };
    let states = [];

    for (let state of coveredStates) {
      let trimmedState = state.toLowerCase().replace(/[-\s]/g, '');
      if (
        formType === FormType.UM_UIM &&
        statesWithMultipleForms.hasOwnProperty(trimmedState)
      ) {
        let ar1 = statesWithMultipleForms[trimmedState];
        for (let formId of ar1) {
          states.push({
            state: `${trimmedState}_${formId}`,
            data: {
              namedInsured: namedInsured,
              policyNumber:
                this.requestService.getPolicyNumberForState(trimmedState),
              nameTitle: `${namedInsured} (${request?.requestDetails?.accountDetail?.title})`,
              issuingCompany:
                this.requestService.getIssuingCompanyForState(trimmedState),
              effectiveDate: policyEffDate,
              isSignatureImagePNG: false,
              signatureImage: '',
              method: 'type',
            },
          });
        }
      } else {
        states.push({
          state: trimmedState,
          data: {
            namedInsured: namedInsured,
            policyNumber:
              this.requestService.getPolicyNumberForState(trimmedState),
            issuingCompany:
              this.requestService.getIssuingCompanyForState(trimmedState),
            nameTitle: `${namedInsured} (${request?.requestDetails?.accountDetail?.title})`,
            effectiveDate: policyEffDate,
            isSignatureImagePNG: false,
            signatureImage: '',
            method: 'type',
          },
        });
      }
    }

    states = this.sortStates(states);
    return states;
  }


  generatePIPPdfAndSendEmail(
    electronicConsent: boolean = true,
    emailType: string = 'thankyou',
  ) {
    Logger.log('Start of generatePIPPdfAndSendEmail');
    let excludeStates = [];
    let obj = this.pipBaseRulesService.pdfDataDump;
    this.removeUnwantedCharacters(FormType.PIP, obj);
    let keys = Object.keys(obj);

    let ar: { state: string; data: any }[] = [];
    for (let key of keys) {
      if (excludeStates.includes(key.toLowerCase())) {
        continue;
      }
      let val = obj[key];
      let pdfObj = {
        state: key.toLowerCase().replace(/[-\s]/g, ''),
        data: {
          ...val,
          namedInsured:
            this.requestService.request?.requestDetails?.accountDetail
              ?.insuredName || '',
          policyNumber: this.requestService.getPolicyNumberForState(key),
          nameTitle: `${this.requestService.request?.requestDetails?.accountDetail?.insuredName} (${this.requestService.request?.requestDetails?.accountDetail?.title})`,
          effectiveDate:
            this.requestService.request?.requestDetails?.policyDetail?.[0]
              ?.effectiveDate || '',
          issuingCompany: this.requestService.getIssuingCompanyForState(key), // Add this line
          isSignatureImagePNG: false,
          signatureImage: '',
          method: 'type',
        },
      };
      ar.push(pdfObj);
    }

    if (ar.length === 0) {
      // if the user wants to download the empty forms, in the uimui-thankyou route.
      ar = this.generateBlankData(FormType.PIP);
    }
    ar = this.sortStates(ar);
    let apiBaseUrl = this.configService.getConfig().pdfBaseUrl;
    const policyInfo = this.getFormattedPolicyInfo();
    const reqObj = {
      formType: 'pip',
      states: ar,
      signature: this.signatureObject,
      emailType: emailType,
      policyInfo: policyInfo,
      brokerEmail:
        this.requestService.request.requestDetails.accountDetail?.brokerEmail,
      brokerCompanyName:
        this.requestService.request.requestDetails.accountDetail
          ?.brokerCompanyName,
      underwriterName:
        this.requestService.request.requestDetails.underWriterDetail?.name,
      electronicConsent: electronicConsent,
      underwriterAddress: this.getFormattedUnderwriterAddress(),
      insuredAddress: this.getFormattedInsuredMailingAddress(),
      operations:
        this.requestService.request.requestDetails.underWriterDetail
          ?.operations,
      insuredTitle:
        this.requestService.request.requestDetails.accountDetail?.title,
      companyName:
        this.requestService.request.requestDetails.accountDetail
          .insuredCompanyName,
      insuredCompanyName:
        this.requestService.request.requestDetails.accountDetail
          .insuredCompanyName,
      eventType:
        this.requestService.request.requestDetails.policySelection?.eventType,
      insuredName:
        this.requestService.request.requestDetails.accountDetail?.insuredName,
      brokerName:
        this.requestService.request.requestDetails.accountDetail?.brokerName,
      uwEmail:
        this.requestService.request.requestDetails.underWriterDetail?.email,
      insuredEmail:
        this.requestService.request.requestDetails.accountDetail?.insuredEmail,
      insuredKey: this.requestService.request.insuredKey,
      insuredPhoneNumber: this.getInsuredPhoneNumber(),
    };
    return this.http.post(
      `${apiBaseUrl}/notification/sendEmailWithPDF`,
      reqObj,
      {
        responseType: 'arraybuffer',
      },
    );
  }
}
